import type { Page } from '../../types';

const nomineeDetails: Page[] = [
  {
    pageType: 'question',
    title: 'Nomination type',
    name: 'nomination-type',
    questions: [
      {
        questionType: 'radio',
        title: 'Is this a team or individual nomination?',
        name: 'nomination-kind',
        hint:
          "A team is made up of more than 1 individual working on an endeavour, project or piece of work together, they do not necessarily have to sit within the same function - a 'team' can consist of two or more staff working on a particular task, through to a whole business unit.",
        options: [
          {
            option: 'Team',
            questions: [
              {
                questionType: 'textInput',
                title: 'Team name',
                name: 'team-name',
                hint:
                  'Add a short team name so the nomination can be identified easily, this could be the actual name of the team or the name of the "project" or "piece of work" the "team" worked on together.',
                rules: {
                  required: true,
                },
              },
            ],
          },
          'Individual',
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'checkbox',
        title: 'Eligibility',
        name: 'eligibility-confirmation',
        options: [
          'I confirm every nominee is a Cabinet Office Digital colleague on Cabinet Office Digital headcount and is eligible for this award.',
        ],
        errorMessages: {
          required:
            'This form can only be used to issue rewards to Cabinet Office Digital staff members',
        },
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Responsible function area',
        name: 'responsible-function-area',
        hint:
          'Which function (functions are led by a DD) panel do you believe should review this nomination.\n\nSince a team can be made up of individuals from different functions, please select the function closest to the work relevant to the nomination',
        hintOption: 'Select a function area',
        options: {
          source: 'athenaPageLoad',
          lookupType: 'staffDirectorates',
          valueField: 'directorate',
          maxRows: 50,
          environment: 'auto',
        },
        disabled: [
          {
            field: 'nomination-kind',
            condition: 'notEquals',
            value: 'Team',
          },
        ],
        rules: {
          required: true,
        },
      },
    ],
  },
  {
    pageType: 'question',
    title: 'Nominee details',
    name: 'individual-nominee',
    changeTypeButton: true,
    disabled: [
      {
        field: 'nomination-kind',
        condition: 'equals',
        value: 'Team',
      },
    ],
    questions: [
      {
        questionType: 'athenaStaffLookup',
        title: 'Search for nominee by name or email',
        name: 'nominee-lookup',
        hint:
          'This is the individual who you are nominating for an award. Please enter their full name and not a nickname, otherwise the record may not be found.\n\nIt may take a few seconds to look up the staff record. Please click on the correct record when visible.',
        searchPlaceholder: 'Please enter a name or email address',
        fuzzyMatch: true,
        matchLimit: 5,
        errorMessages: {
          required: 'You must click on a user from the lookup results',
        },
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Nominee full name',
        name: 'nominee-full-name',
        readOnly: true,
        hideOptionalLabel: true,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Email address of the nominee',
        name: 'nominee-email',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Nominee Job Title',
        name: 'nominee-job-title',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Nominee SOP staff number',
        name: 'nominee-staff-number',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Cost Centre',
        name: 'nominee-cost-centre',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Substantive Grade of the nominee',
        name: 'nominee-grade',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Nominee function',
        name: 'nominee-function',
        readOnly: true,
        hideOptionalLabel: true,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Nominee line manager email address',
        name: 'nominee-line-manager-email',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'radio',
        title: 'Is this information correct?',
        name: 'nominee-data-correct',
        options: [
          'Yes',
          {
            option: 'No',
            questions: [
              {
                questionType: 'select',
                title: 'What data is incorrect?',
                name: 'nominee-data-incorrect-field',
                hintOption: 'Select an option',
                options: [
                  'Nominee full name',
                  'Nominee email address',
                  'Nominee Job Title',
                  'Nominee SOP staff number',
                  'Cost Centre',
                  'Substantive Grade of the nominee',
                  'Nominee function',
                  'Nominee line manager email address',
                  'Multiple errors / something else',
                ],
                rules: {
                  required: true,
                },
              },
              {
                questionType: 'textarea',
                title: 'Please provide the correct information or further details',
                name: 'nominee-data-incorrect-reason',
                textRows: 4,
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        errorMessages: {
          required: 'This field is mandatory',
        },
        rules: {
          required: true,
        },
      },
    ],
  },
  {
    pageType: 'list',
    title: 'Team members',
    name: 'team-nominee',
    listLabel: 'Team member',
    addButton: true,
    changeTypeButton: true,
    minimumRequiredListItems: 2,
    minimumRequiredListItemsErrorMessage:
      "You need to have 2 or more nominees for a Team nomination. Click 'Add another' to add another nominee or click 'Change type' to submit a nomination for an individual instead.",
    disabled: [
      {
        field: 'nomination-kind',
        condition: 'equals',
        value: 'Individual',
      },
    ],
    description:
      'Add every team member who should be included in the nomination. For team nominations, the reward amount entered later should be the total amount for the whole team.<br /><br />' +
      'When awarding a thank you Reward Voucher to a team (2 or more individuals), there is a cap of £200 for the team.<br /><br />' +
      'When awarding an IYB to a team (2 or more individuals), there is a cap of £1000 for the team.<br /><br />',
    questions: [
      {
        questionType: 'athenaStaffLookup',
        title: 'Search for nominee by name or email',
        name: 'nominee-lookup',
        hint:
          'This is the individual who you are nominating for an award. Please enter their full name and not a nickname, otherwise the record may not be found.\n\nIt may take a few seconds to look up the staff record. Please click on the correct record when visible.',
        searchPlaceholder: 'Please enter a name or email address',
        fuzzyMatch: true,
        matchLimit: 5,
        errorMessages: {
          required: 'You must click on a user from the lookup results',
        },
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Nominee full name',
        name: 'nominee-full-name',
        readOnly: true,
        hideOptionalLabel: true,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Email address of the nominee',
        name: 'nominee-email',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Nominee Job Title',
        name: 'nominee-job-title',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Nominee SOP staff number',
        name: 'nominee-staff-number',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Cost Centre',
        name: 'nominee-cost-centre',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Substantive Grade of the nominee',
        name: 'nominee-grade',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'textInput',
        title: 'Nominee function',
        name: 'nominee-function',
        readOnly: true,
        hideOptionalLabel: true,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Nominee line manager email address',
        name: 'nominee-line-manager-email',
        readOnly: true,
        hideOptionalLabel: true,
      },
      {
        questionType: 'radio',
        title: 'Is this information correct?',
        name: 'nominee-data-correct',
        options: [
          'Yes',
          {
            option: 'No',
            questions: [
              {
                questionType: 'select',
                title: 'What data is incorrect?',
                name: 'nominee-data-incorrect-field',
                hintOption: 'Select an option',
                options: [
                  'Nominee full name',
                  'Nominee email address',
                  'Nominee Job Title',
                  'Nominee SOP staff number',
                  'Cost Centre',
                  'Substantive Grade of the nominee',
                  'Nominee function',
                  'Nominee line manager email address',
                  'Multiple errors / something else',
                ],
                rules: {
                  required: true,
                },
              },
              {
                questionType: 'textarea',
                title: 'Please provide the correct information or further details',
                name: 'nominee-data-incorrect-reason',
                textRows: 4,
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        errorMessages: {
          required: 'This field is mandatory',
        },
        rules: {
          required: true,
        },
      },
    ],
  },
];

export default nomineeDetails;
